import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canManageSafetyCaseArtifacts } from '../auth/access';
import { ensureUiScope } from '../services/api';
import { reportsApi, type SafetyCaseRecord } from '../services/reportsApi';
import { safetyScanVerifyPath } from '../domain/acceptance/scanVerifyNavigation';
import { resolveBuyerCustomerScanDownloadReportId } from '../domain/safetyCases/scanBuyerResultHierarchy';
import InvestigationAlignmentCard from '../components/safetyReports/InvestigationAlignmentCard';
import ClaimsRoiAlignmentCard from '../components/safetyReports/ClaimsRoiAlignmentCard';
import SafetyControlReviewCard from '../components/safetyReports/SafetyControlReviewCard';
import { resolveInvestigationAlignmentFromSafetyCase } from '../domain/reportContext/investigationAlignment';
import { resolveClaimsRoiFromSafetyCase } from '../domain/reportContext/claimsRoiAlignment';
import { resolveSafetyControlReviewFromSafetyCase } from '../domain/reportContext/safetyControlReview';
import {
  ACCEPTANCE_UI_LABELS,
  resolveSignedSafetyScanReportId,
} from '../utils/acceptanceHierarchyNavigation';
import {
  buildCreateDraftPayloadFromHandoff,
  buildHandoffFromSafetyCaseRecord,
} from '../domain/safetyCases/safetyScanWorkspaceHandoff';
import {
  buildSafetyCaseReviewNewActivityPath,
  buildSafetyCaseWorklistSummary,
  currentCaseStateLabel,
  enrichSafetyCasesWithDashboardRows,
  formatSafetyCaseActionOwnerLabel,
  hazardCategoryLabel,
  hasNewActivitySinceSignedScan,
  latestSignedSafetyScanLabel,
  newActivityStateLabel,
  nextCaseStepLabel,
  originatingRiskPatternLabel,
  readAreaLabel,
  readCameraLabel,
  readDeviceLabel,
  readSiteLabel,
  readZoneLabel,
  safetyCaseCardTitle,
  safetyCaseDedupeKey,
  safetyCaseOutcomeStateLabel,
  safetyCaseReviewStateLabel,
  scanHistoryCountLabel,
} from '../domain/safetyCases/safetyCaseWorklist';
import { PlatformWorkflowStrip } from '../components/platform/PlatformWorkflowStrip';
import ConditionWorkspacePanel from '../components/safetyCases/ConditionWorkspacePanel';

const SAFETY_CASES_SUBTITLE =
  'Manage confirmed Safety Cases. Open a case to review recommendations, decisions, corrective actions, new activity, outcomes, and linked Safety Scans.';

function readBuyerScanMeaning(item: SafetyCaseRecord): string | null {
  const scope = (item.scope && typeof item.scope === 'object') ? item.scope as Record<string, unknown> : {};
  const context = scope.policy_report_context && typeof scope.policy_report_context === 'object'
    ? scope.policy_report_context as Record<string, unknown>
    : scope.report_context && typeof scope.report_context === 'object'
      ? scope.report_context as Record<string, unknown>
      : null;
  return String(
    context?.hazard_exposure_label
    || context?.short_exposure_label
    || context?.exposure_category_label
    || '',
  ).trim() || null;
}

const SafetyCaseCard: React.FC<{
  item: SafetyCaseRecord;
  siteId: string;
  onOpenCase: (item: SafetyCaseRecord) => void;
  onReviewNewActivity: (item: SafetyCaseRecord) => void;
  onViewSignedScan: (item: SafetyCaseRecord) => void;
  onCreateUpdatedScan: (item: SafetyCaseRecord) => void;
  onMeasureDetectionReadiness: (item: SafetyCaseRecord) => void;
  canManageSafetyCases: boolean;
}> = ({
  item,
  siteId,
  onOpenCase,
  onReviewNewActivity,
  onViewSignedScan,
  onCreateUpdatedScan,
  onMeasureDetectionReadiness,
  canManageSafetyCases,
}) => {
  const areaLabel = readAreaLabel(item);
  const zoneLabel = readZoneLabel(item);
  const cameraLabel = readCameraLabel(item);
  const deviceLabel = readDeviceLabel(item);
  const hasNewActivity = hasNewActivitySinceSignedScan(item);
  const canMeasure = canManageSafetyCases && Boolean(String(item.workflow_id || '').trim());
  const signedScanAvailable = Boolean(resolveSignedSafetyScanReportId({ ...item, report_status: 'finalized' }));
  const reviewStateLabel = safetyCaseReviewStateLabel(item);
  const outcomeStateLabel = safetyCaseOutcomeStateLabel(item);
  const investigationAlignment = resolveInvestigationAlignmentFromSafetyCase(item);
  const claimsRoiAlignment = resolveClaimsRoiFromSafetyCase(item);
  const safetyControlReview = resolveSafetyControlReviewFromSafetyCase(item);
  const buyerScanMeaning = readBuyerScanMeaning(item);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid={`safety-case-card-${safetyCaseDedupeKey(item)}`}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {safetyCaseCardTitle(item)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`Originating Risk Pattern: ${originatingRiskPatternLabel(item)}`}
              </Typography>
              {buyerScanMeaning ? (
                <Typography variant="body2" color="text.secondary">
                  {`Scan meaning: ${buyerScanMeaning}`}
                </Typography>
              ) : null}
            </Box>
            <Chip size="small" color="warning" variant="outlined" label={currentCaseStateLabel(item)} />
          </Stack>

          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
            <Typography variant="body2">{`Hazard Category: ${hazardCategoryLabel(item)}`}</Typography>
            <Typography variant="body2">{`Site: ${readSiteLabel(item, siteId)}`}</Typography>
            <Typography variant="body2">{`Zone / Area: ${areaLabel || zoneLabel}`}</Typography>
            <Typography variant="body2">
              {cameraLabel ? `Camera: ${cameraLabel}` : deviceLabel ? `Device: ${deviceLabel}` : 'Camera / Device: Not recorded'}
            </Typography>
            <Typography variant="body2">{`Case state: ${currentCaseStateLabel(item)}`}</Typography>
            {reviewStateLabel ? (
              <Typography variant="body2">{`Review state: ${reviewStateLabel}`}</Typography>
            ) : null}
            {outcomeStateLabel ? (
              <Typography variant="body2">{`Outcome state: ${outcomeStateLabel}`}</Typography>
            ) : null}
            <Typography variant="body2">{`Action owner: ${formatSafetyCaseActionOwnerLabel(item)}`}</Typography>
            <Typography variant="body2">{`Latest Signed Safety Scan: ${latestSignedSafetyScanLabel(item)}`}</Typography>
            <Typography variant="body2">{newActivityStateLabel(item)}</Typography>
            <Typography variant="body2">{`Scan history: ${scanHistoryCountLabel(item)}`}</Typography>
          </Box>

          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>Next step:</Box>
            {' '}
            {nextCaseStepLabel(item)}
          </Typography>

          <InvestigationAlignmentCard
            alignment={investigationAlignment}
            compact
            title="Investigation alignment"
            testId={`safety-case-alignment-${safetyCaseDedupeKey(item)}`}
          />
          <ClaimsRoiAlignmentCard
            alignment={claimsRoiAlignment}
            compact
            title="Claims / ROI alignment"
            testId={`safety-case-claims-roi-${safetyCaseDedupeKey(item)}`}
          />
          <SafetyControlReviewCard
            alignment={safetyControlReview}
            compact
            title="Safety control review"
            testId={`safety-case-safety-control-review-${safetyCaseDedupeKey(item)}`}
          />

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {canMeasure ? (
              <Button
                variant="contained"
                size="small"
                data-testid={`measure-detection-readiness-${safetyCaseDedupeKey(item)}`}
                onClick={() => void onMeasureDetectionReadiness(item)}
              >
                Measure detection readiness
              </Button>
            ) : null}
            <Button variant={canMeasure ? 'outlined' : 'contained'} size="small" onClick={() => onOpenCase(item)}>
              {ACCEPTANCE_UI_LABELS.openLiveSafetyCase}
            </Button>
            {hasNewActivity && canManageSafetyCases ? (
              <Button variant="outlined" size="small" onClick={() => onReviewNewActivity(item)}>
                {ACCEPTANCE_UI_LABELS.reviewNewActivity}
              </Button>
            ) : null}
            {signedScanAvailable ? (
              <Button variant="outlined" size="small" onClick={() => void onViewSignedScan(item)}>
                {ACCEPTANCE_UI_LABELS.viewLatestSignedScan}
              </Button>
            ) : null}
            {hasNewActivity && canManageSafetyCases ? (
              <Button variant="outlined" size="small" onClick={() => void onCreateUpdatedScan(item)}>
                {ACCEPTANCE_UI_LABELS.createUpdatedSafetyScan}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const AcceptanceSafetyCasesHome: React.FC = () => {
  const navigate = useNavigate();
  const { effectiveUser: user } = useAuth();
  const canManageSafetyCases = canManageSafetyCaseArtifacts(user);
  const scope = useMemo(() => ensureUiScope(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cases, setCases] = useState<SafetyCaseRecord[]>([]);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [casesResponse, dashboardResponse] = await Promise.all([
        reportsApi.listSafetyCases({ limit: 200 }),
        reportsApi.getSafetyDashboard({
          org_id: scope.orgId,
          site_id: scope.siteId,
          limit: 200,
        }),
      ]);
      setCases(enrichSafetyCasesWithDashboardRows(
        casesResponse.safety_cases || [],
        dashboardResponse.workflows || [],
      ));
    } catch (err: any) {
      setError(err?.message || 'Safety Cases are unavailable right now.');
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [scope.orgId, scope.siteId]);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  const worklistSummary = useMemo(
    () => buildSafetyCaseWorklistSummary(cases),
    [cases],
  );

  const openCase = useCallback((item: SafetyCaseRecord) => {
    const q = new URLSearchParams();
    if (scope.orgId) q.set('org_id', scope.orgId);
    if (scope.siteId) q.set('site_id', scope.siteId);
    if (item.workflow_id) q.set('workflow_id', String(item.workflow_id));
    const targetCaseId = String(item.safety_case_id || item.finding_id || '').trim();
    if (targetCaseId) q.set('case_id', targetCaseId);
    if (item.representative_incident_id) q.set('incident_id', String(item.representative_incident_id));
    navigate(`/acceptance/cases/${encodeURIComponent(targetCaseId)}?${q.toString()}`);
  }, [navigate, scope.orgId, scope.siteId]);

  const reviewNewActivity = useCallback((item: SafetyCaseRecord) => {
    navigate(buildSafetyCaseReviewNewActivityPath(item, scope));
  }, [navigate, scope]);

  const viewSignedScan = useCallback((item: SafetyCaseRecord) => {
    const reportId = resolveSignedSafetyScanReportId({ ...item, report_status: 'finalized' });
    if (!reportId) return;
    navigate(safetyScanVerifyPath(reportId, {
      orgId: scope.orgId,
      siteId: scope.siteId || item.site_id,
      safetyCaseId: String(item.safety_case_id || item.finding_id || '').trim() || null,
    }));
  }, [navigate, scope]);

  const openSafetyScanWorkspaceForCase = useCallback(async (item: SafetyCaseRecord) => {
    const workflowId = String(item.workflow_id || '').trim();
    if (!workflowId) return;
    const handoff = buildHandoffFromSafetyCaseRecord(item, scope, { isUpdatedScan: hasNewActivitySinceSignedScan(item) });
    if (item.active_draft_report_id) {
      const scanHistory = (item as { scan_history?: Array<Record<string, unknown>> }).scan_history;
      const reportId = resolveBuyerCustomerScanDownloadReportId({
        openedReportId: String(item.active_draft_report_id),
        scanHistory,
        latestSignedReportId: item.latest_finalized_report_id,
      }) || String(item.active_draft_report_id);
      navigate(safetyScanVerifyPath(reportId, {
        orgId: scope.orgId,
        siteId: scope.siteId || item.site_id,
        safetyCaseId: String(item.safety_case_id || item.finding_id || '').trim() || null,
      }));
      return;
    }

    const response = await reportsApi.createOrResumeSafetyReportDraft(
      workflowId,
      buildCreateDraftPayloadFromHandoff(handoff),
    );
    const createdReportId = String(response.report?.report_id || '').trim();
    if (!createdReportId) return;
    navigate(safetyScanVerifyPath(createdReportId, {
      orgId: scope.orgId,
      siteId: scope.siteId || item.site_id,
      safetyCaseId: String(item.safety_case_id || item.finding_id || '').trim() || null,
    }));
  }, [navigate, scope]);

  const createUpdatedScan = useCallback(async (item: SafetyCaseRecord) => {
    await openSafetyScanWorkspaceForCase(item);
  }, [openSafetyScanWorkspaceForCase]);

  const measureDetectionReadiness = useCallback(async (item: SafetyCaseRecord) => {
    await openSafetyScanWorkspaceForCase(item);
  }, [openSafetyScanWorkspaceForCase]);

  const kpiCards = [
    {
      label: 'Safety Cases',
      value: worklistSummary.totalCount,
      helper: 'Confirmed Safety Cases currently managed for this site.',
    },
    {
      label: 'Cases Needing Review',
      value: worklistSummary.needingReviewCount,
      helper: 'Cases waiting for evidence review, recommendation decisions, or the next case update.',
    },
    {
      label: 'Cases With New Activity',
      value: worklistSummary.newActivityCount,
      helper: 'Cases with activity after their latest Signed Safety Scan was signed.',
    },
    {
      label: 'Validated Outcomes',
      value: worklistSummary.validatedOutcomesCount,
      helper: 'Validated outcome records logged across visible Safety Cases.',
    },
  ];

  return (
    <Stack spacing={3} sx={{ width: '100%', maxWidth: 1440, pb: 2 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.75, letterSpacing: '-0.01em' }}>
          Safety Cases
        </Typography>
        <Typography color="text.secondary">
          {SAFETY_CASES_SUBTITLE}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {`Site: ${scope.siteId || 'unknown'} · ${worklistSummary.totalCount} managed Safety Case${worklistSummary.totalCount === 1 ? '' : 's'}`}
        </Typography>
      </Box>

      <PlatformWorkflowStrip currentStep="measure" secondaryStep="case" />

      <Alert severity="info" data-testid="safety-cases-measure-entry-hint">
        Measure comes after Configure. Use <strong>Measure detection readiness</strong> on a Safety Case to open
        Safety Scan Workspace and admit detector evaluation before Scan / Review.
      </Alert>

      {scope.siteId ? <ConditionWorkspacePanel siteId={scope.siteId} /> : null}

      {error ? <Alert severity="warning">{error}</Alert> : null}

      {!canManageSafetyCases ? (
        <Alert severity="info">
          Read-only Safety Case access. Open cases and view signed scans; create, assign, close, and sign actions are hidden.
        </Alert>
      ) : null}

      <Box
        data-testid="safety-cases-kpi-row"
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' } }}
      >
        {kpiCards.map((card) => (
          <Card key={card.label} variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {loading ? <Skeleton width={40} /> : card.value}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{card.label}</Typography>
              <Typography variant="body2" color="text.secondary">{card.helper}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Safety Cases
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Open a case to manage recommendations, decisions, corrective actions, new activity, and linked Safety Scans.
          </Typography>
        </Box>

        {loading ? (
          <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 3 }} />
        ) : worklistSummary.managedCases.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No Safety Cases are currently managed for this site.
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
            {worklistSummary.managedCases.map((item) => (
              <SafetyCaseCard
                key={safetyCaseDedupeKey(item)}
                item={item}
                siteId={scope.siteId}
                onOpenCase={openCase}
                onReviewNewActivity={reviewNewActivity}
                onViewSignedScan={viewSignedScan}
                onCreateUpdatedScan={createUpdatedScan}
                onMeasureDetectionReadiness={measureDetectionReadiness}
                canManageSafetyCases={canManageSafetyCases}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default AcceptanceSafetyCasesHome;
